import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { profileApi } from '../lib/api'
import { read as storageRead, write as storageWrite } from './storage'
import { adaptFromProfile, adaptToProfile } from './adapters'
import { DEFAULT_PREFERENCES, type ReaderPreferences } from './types'
import type { ProfilePreferences } from './adapters'

type ContextValue = {
  prefs: ReaderPreferences
  /**
   * Update a local reader preference. By default writes to localStorage AND
   * schedules a debounced profile sync (for fields not in LOCAL_ONLY_PREFS).
   *
   * Pass `{ skipSync: true }` when ANOTHER code path is already responsible
   * for the profile sync — typically the SettingsPage, which keeps its own
   * `UserPreferences` mirror and flushes the entire profile object on save.
   * Without skipSync, calling setPref from SettingsPage triggers a second
   * profile PUT that races the first; last write wins, and the two payloads
   * can carry different field sets.
   */
  setPref: <K extends keyof ReaderPreferences>(
    key: K,
    value: ReaderPreferences[K],
    options?: { skipSync?: boolean },
  ) => void
  isHydrating: boolean
}

const PreferencesContext = createContext<ContextValue | null>(null)

const SYNC_DEBOUNCE_MS = 300
const SYNC_RETRY_MS = 5_000

// Fields that DON'T round-trip to the user's profile. Changing one of these
// skips the debounced profile sync entirely — saves a wasted PUT /profile
// request whose body wouldn't include the field anyway (the adapter strips
// it). Keep this set in sync with `adapters.ts` (anything not in
// adaptToProfile belongs here). Module-scope so the reference is stable
// across renders and doesn't appear in any hook dep arrays.
const LOCAL_ONLY_PREFS: ReadonlySet<keyof ReaderPreferences> = new Set([
  'wbwEnabled',
  'viewMode',
  'soundEffectsEnabled',
  'bismillahBeforeSurah',
  'offlineDataBudgetMB',
  'autoDownloadOnPlay',
])

interface ProviderProps {
  children: ReactNode
}

export function PreferencesProvider({ children }: ProviderProps) {
  const { isAuthenticated } = useAuth()
  const [prefs, setPrefs] = useState<ReaderPreferences>(() => storageRead() ?? DEFAULT_PREFERENCES)
  const [isHydrating, setHydrating] = useState(false)

  // ---- async profile-fetch overlay (only when authenticated) ----
  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    setHydrating(true)
    profileApi.getProfile()
      .then((user) => {
        if (cancelled) return
        const profileData = (user as { preferences?: ProfilePreferences }).preferences
        const fromProfile = adaptFromProfile(profileData)
        if (Object.keys(fromProfile).length === 0) return
        setPrefs((local) => ({ ...local, ...fromProfile }))
      })
      .catch((err) => {
        if (import.meta.env.DEV) console.warn('Profile prefs fetch failed; using local prefs', err)
      })
      .finally(() => {
        if (!cancelled) setHydrating(false)
      })
    return () => {
      cancelled = true
      setHydrating(false)
    }
  }, [isAuthenticated])

  // ---- debounced profile sync ----
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPrefsRef = useRef<ReaderPreferences | null>(null)
  const isAuthRef = useRef(isAuthenticated)
  isAuthRef.current = isAuthenticated

  const flushSync = useCallback((isRetry: boolean) => {
    if (!isAuthRef.current) return
    const next = pendingPrefsRef.current
    if (!next) return
    profileApi.updateProfile({ preferences: adaptToProfile(next) } as Parameters<typeof profileApi.updateProfile>[0])
      .then(() => {
        pendingPrefsRef.current = null
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current)
          retryTimerRef.current = null
        }
      })
      .catch((err) => {
        if (isRetry) {
          if (import.meta.env.DEV) console.warn('Failed to sync preferences to profile (after retry)', err)
          pendingPrefsRef.current = null
          retryTimerRef.current = null
          return
        }
        retryTimerRef.current = setTimeout(() => flushSync(true), SYNC_RETRY_MS)
      })
  }, [])

  const scheduleSync = useCallback((next: ReaderPreferences) => {
    pendingPrefsRef.current = next
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null
      flushSync(false)
    }, SYNC_DEBOUNCE_MS)
  }, [flushSync])

  // Cancel pending sync if the user logs out.
  useEffect(() => {
    if (isAuthenticated) return
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    pendingPrefsRef.current = null
  }, [isAuthenticated])

  // Final cleanup on unmount: ensure no dangling timers.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [])

  const setPref = useCallback(<K extends keyof ReaderPreferences>(
    key: K,
    value: ReaderPreferences[K],
    options?: { skipSync?: boolean },
  ) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value }
      storageWrite(next)
      // Skip the profile sync when the changed field is local-only OR the
      // caller explicitly opted out (because another save path already
      // owns the profile round-trip — see the SettingsPage reciter selector).
      const localOnly = LOCAL_ONLY_PREFS.has(key)
      const skipSync = options?.skipSync === true
      if (isAuthRef.current && !localOnly && !skipSync) scheduleSync(next)
      return next
    })
  }, [scheduleSync])

  const value = useMemo<ContextValue>(
    () => ({ prefs, setPref, isHydrating }),
    [prefs, setPref, isHydrating]
  )

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

export function usePreferences(): ContextValue {
  const ctx = useContext(PreferencesContext)
  if (!ctx) {
    throw new Error('usePreferences must be used inside <PreferencesProvider>')
  }
  return ctx
}
